import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { UsageStepFormInputs } from './types'
import { updateStoreForUsageStep } from './utils'
import UsageForm from 'features/usage-form/UsageForm'
import { determineDefaultUsageFormValues, isProducerOverCapacity } from 'features/usage-form/utils'
import { CustomerFlows, InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { determineSimulationUsage, requestAndSavePropositions } from '../utils'
import { useGetProducersQuery } from 'store/api/bolt-api'
import {
  nextStep,
  setDidSimulation,
  setShowPlentyPropositions,
  toggleContactMe,
  updateInput,
  updateSimulatedPropositions
} from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { getUsableProducts } from 'utils/products'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { filterAndSortProducers } from 'utils/producers'
import { setSelectedProducer } from 'store/app/slice'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { PRODUCT_CONFIG_OPTIONS, PRODUCTS_CONFIG, SIMULATION_TYPES } from '@boltenergy-be/shared'
import { CommonTrackingParams } from 'types/tracking'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from 'constants/tracking'

const UsageStep = () => {
  // Redux store
  const { inputs, flows, propositions } = useStoreSelector((store) => store.customer)
  const { selectedProducer } = useStoreSelector((store) => store.app)
  const { contactMe } = flows.simulation
  const dispatch = useStoreDispatch()

  // Next Router
  const { locale } = useRouter()

  // RTK queries
  const { data: producers } = useGetProducersQuery()

  // Posthog
  const showRoiBanner = useFeatureFlagVariantKey(FEATURE_FLAGS.injectionRoiSimulation) === 'with-roi'

  // React Hook Form
  const defaultValues = useMemo(() => determineDefaultUsageFormValues(inputs), [inputs])
  const hookForm = useForm<UsageStepFormInputs>({
    defaultValues,
    mode: 'onBlur'
  })
  const { getValues } = hookForm

  // Constants
  const producer = producers?.[selectedProducer]

  /**
   * Handles the next step click
   */
  const finishSimulation = async () => {
    const startTime = performance.now()

    // Set the simulation type to pricing simulation because at this point it is always a pricing simulation
    dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
        value: SIMULATION_TYPES.pricingSimulation
      })
    )

    const data = getValues()

    updateStoreForUsageStep(data)

    // If the producer is over capacity, we need to select a new producer
    const usage = determineSimulationUsage(data.usageGrade, data)
    if (isProducerOverCapacity(producer, { ...inputs.simulation, usage })) {
      const newProducerForCapacity = filterAndSortProducers({
        capacity: getTotalElectricityVolumeForMeterType(inputs.simulation.meterType, usage),
        language: locale as Language,
        producers: Object.values(producers)
      })[0]

      dispatch(setSelectedProducer({ producerSlug: newProducerForCapacity.slug }))
    }

    // Reset propositions
    dispatch(updateSimulatedPropositions({ propositions: null }))

    // Get the products to simulate
    const usableProducts = getUsableProducts({ region: inputs.simulation.region })
    const specificProductIsAvailable =
      propositions?.simulateSpecificProduct && usableProducts?.includes(propositions?.simulateSpecificProduct)
    const specificProductCounterpart =
      (!!propositions?.simulateSpecificProduct &&
        PRODUCTS_CONFIG[propositions.simulateSpecificProduct]?.[PRODUCT_CONFIG_OPTIONS.counterpart]) ||
      undefined
    const productsToSimulate = specificProductIsAvailable ? [propositions?.simulateSpecificProduct] : usableProducts

    // If only Plenty products are available, show Plenty propositions
    if (usableProducts.every((product) => PRODUCTS_CONFIG[product].isPlentyProduct)) {
      dispatch(setShowPlentyPropositions(true))
    }

    // Add counterpart product to products to simulate if not already included
    if (specificProductCounterpart && !productsToSimulate.includes(specificProductCounterpart)) {
      productsToSimulate.push(specificProductCounterpart)
    }

    // Request the propositions for both variable products & set the didSimulation to true when successfully fetched propositions
    await requestAndSavePropositions({
      onSuccess: () => dispatch(setDidSimulation()),
      products: productsToSimulate
    })

    const totalDuration = performance.now() - startTime
    // Track 'Simulation Finished' event @Semetis
    trackGTMEvent('simulation_finished', {
      solar_panel: !!data.hasSolarPanels,
      product_need: inputs.deliveryAddress.needsGas ? 'gaz + electricity' : 'electricity',
      costumer_type: inputs.personalData.isCompany ? 'company' : 'private',
      postcode: inputs.deliveryAddress.address.postalCode,
      duration_ms: totalDuration
    })
    trackEvent(
      { category: 'simulation', action: 'finish' },
      {
        [CommonTrackingParams.DURATION]: totalDuration,
        usage_grade: data.usageGrade
      }
    )

    // Toggle the contactMe in the store if it is set to true
    if (contactMe) dispatch(toggleContactMe(contactMe))

    dispatch(nextStep({ flow: CustomerFlows.SIMULATION }))
  }

  return (
    <FormProvider {...hookForm}>
      <UsageForm onBlur={updateStoreForUsageStep} onSubmit={finishSimulation} hasInjectionRoi={showRoiBanner} />
    </FormProvider>
  )
}

export default UsageStep
